import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import Select from 'react-select';
import { getApiBaseUrl } from '../../config/apiConfig';
import { DataContext } from '../context/DataContext';
import {
  FaChartBar,
  FaSearch,
  FaComments,
  FaBolt,
  FaPlay,
  FaCheck,
  FaRobot,
  FaUser,
  FaClock,
} from 'react-icons/fa';
import { FiXCircle } from 'react-icons/fi';

const reactSelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: 40,
    borderColor: '#D1D5DB',
    boxShadow: 'none',
    ':hover': { borderColor: '#6366F1' },
  }),
};

const STAT_PALETTE = {
  indigo: 'bg-indigo-50 text-indigo-600',
  green: 'bg-green-50 text-green-600',
  purple: 'bg-purple-50 text-purple-600',
  blue: 'bg-blue-50 text-blue-600',
};

export default function AutomationAnalytics() {
  const { allEventsSummary, fetchAllEventNames } = useContext(DataContext);
  const [eventId, setEventId] = useState('');
  const [uid, setUid] = useState('');
  const [analytics, setAnalytics] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingConv, setLoadingConv] = useState(false);
  const [error, setError] = useState(null);

  const token = Cookies.get('fbauthToken');
  const API = getApiBaseUrl();

  useEffect(() => {
    if (allEventsSummary.length === 0) {
      fetchAllEventNames();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (eventId) params.append('event_id', eventId);
      if (uid) params.append('uid', uid);
      const res = await axios.get(
        `${API}/api/v2/automation/analytics?${params}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setAnalytics(res.data);
      if (eventId) {
        fetchConversations(eventId);
      }
    } catch (err) {
      setError('Failed to load analytics');
    }
    setLoading(false);
  };

  const fetchConversations = async (eid) => {
    setLoadingConv(true);
    try {
      const res = await axios.get(
        `${API}/api/v2/automation/conversations?event_id=${eid}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setConversations(res.data);
    } catch (err) {
      console.error('Failed to load conversations', err);
    }
    setLoadingConv(false);
  };

  const eventOptions = allEventsSummary.map((e) => ({
    value: String(e.event_id),
    label: `${e.event_name} (${e.event_id})`,
  }));

  const selectedEventOption =
    eventOptions.find((opt) => String(opt.value) === String(eventId)) || null;

  const statCards = analytics
    ? [
        {
          label: 'Total Workflows',
          value:
            analytics.totalWorkflows ?? analytics.total_workflows ?? 0,
          icon: <FaBolt />,
          color: 'indigo',
        },
        {
          label: 'Active Workflows',
          value:
            analytics.activeWorkflows ?? analytics.active_workflows ?? 0,
          icon: <FaPlay />,
          color: 'green',
        },
        {
          label: 'Total Conversations',
          value:
            analytics.totalConversations ??
            analytics.total_conversations ??
            0,
          icon: <FaComments />,
          color: 'purple',
        },
        {
          label: 'Completed',
          value: analytics.conversationsByStatus?.completed ?? 0,
          icon: <FaCheck />,
          color: 'blue',
        },
      ]
    : [];

  return (
    <div className="lg:p-1 min-h-screen lg:mr-[2vw] bg-gray-50">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h2 className="text-3xl font-bold text-indigo-800 flex items-center gap-2">
            <FaChartBar />
            <span>Automation Analytics</span>
          </h2>
          <p className="text-gray-600 mt-1">
            Monitor AI automation performance across events and users.
          </p>
        </div>
      </div>

      {/* Filter card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
          <div className="md:col-span-6">
            <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wider">
              Event
            </label>
            <Select
              options={eventOptions}
              value={selectedEventOption}
              onChange={(opt) => setEventId(opt ? opt.value : '')}
              placeholder="Search event…"
              className="text-sm"
              styles={reactSelectStyles}
              isSearchable
              isClearable
            />
          </div>

          <div className="md:col-span-4">
            <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wider">
              User UID (Optional)
            </label>
            <div className="flex items-center gap-2 bg-white border border-gray-300 rounded-lg px-3 py-2 focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-indigo-500">
              <FaSearch className="text-gray-400 text-xs" />
              <input
                className="flex-1 text-sm focus:outline-none bg-transparent"
                placeholder="Enter UID…"
                value={uid}
                onChange={(e) => setUid(e.target.value)}
              />
            </div>
          </div>

          <div className="md:col-span-2">
            <button
              onClick={fetchAnalytics}
              disabled={loading}
              type="button"
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
            >
              {loading ? 'Loading…' : 'Fetch Analytics'}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 rounded-lg text-sm bg-red-50 text-red-700 border border-red-200 flex items-center gap-2">
          <FiXCircle />
          <span className="font-medium">{error}</span>
        </div>
      )}

      {/* Empty state */}
      {!analytics && !loading && (
        <div className="bg-white rounded-xl border border-dashed border-gray-300 py-16 text-center">
          <FaChartBar className="mx-auto text-5xl mb-3 text-gray-300" />
          <h3 className="text-gray-600 font-medium">No data loaded</h3>
          <p className="text-gray-400 text-sm">
            Select an event and click Fetch Analytics to start.
          </p>
        </div>
      )}

      {analytics && (
        <>
          {/* Stat cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {statCards.map((card, idx) => (
              <div
                key={idx}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-3"
              >
                <div
                  className={`p-2.5 rounded-lg text-xl ${STAT_PALETTE[card.color]}`}
                >
                  {card.icon}
                </div>
                <div>
                  <p className="text-sm text-gray-500">{card.label}</p>
                  <p className="text-2xl font-bold text-gray-800">
                    {card.value}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Two-column layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Status breakdown */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden h-full">
                <div className="border-b border-gray-200 px-4 py-3 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                    <span className="w-1 h-4 bg-indigo-500 rounded-full"></span>
                    Status Breakdown
                  </h3>
                </div>
                <div className="p-4 space-y-2">
                  {analytics.conversationsByStatus &&
                    Object.entries(analytics.conversationsByStatus).map(
                      ([status, count]) => (
                        <div
                          key={status}
                          className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            <span
                              className={`w-2.5 h-2.5 rounded-full ${
                                status === 'active'
                                  ? 'bg-green-500'
                                  : status === 'completed'
                                    ? 'bg-blue-500'
                                    : status === 'paused'
                                      ? 'bg-yellow-500'
                                      : 'bg-gray-400'
                              }`}
                            />
                            <span className="text-sm text-gray-600 capitalize font-medium">
                              {status}
                            </span>
                          </div>
                          <span className="text-sm font-bold text-gray-800">
                            {count}
                          </span>
                        </div>
                      ),
                    )}
                  {(!analytics.conversationsByStatus ||
                    Object.keys(analytics.conversationsByStatus).length ===
                      0) && (
                    <p className="text-sm text-gray-400 italic text-center py-6">
                      No status data available
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Recent conversations */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden h-full">
                <div className="border-b border-gray-200 px-4 py-3 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                    <span className="w-1 h-4 bg-purple-500 rounded-full"></span>
                    Recent AI Interactions
                  </h3>
                  {loadingConv && (
                    <span className="text-xs text-gray-500">Loading…</span>
                  )}
                </div>

                <div className="p-4">
                  {!loadingConv && conversations.length === 0 && (
                    <div className="text-center py-10 text-gray-400">
                      <p className="text-sm italic">
                        No recent interactions found for this event.
                      </p>
                    </div>
                  )}

                  {conversations.length > 0 && (
                    <div className="space-y-3 max-h-[560px] overflow-y-auto pr-1">
                      {conversations.map((conv, idx) => (
                        <div
                          key={idx}
                          className="border border-gray-200 rounded-lg p-4 bg-gray-50/40 hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex justify-between items-start mb-3 gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white">
                                <FaUser size={12} />
                              </div>
                              <div className="min-w-0">
                                <div className="text-sm font-semibold text-gray-800 truncate">
                                  {conv.user_name || 'Instagram User'}
                                </div>
                                <div className="text-[11px] text-gray-400 flex items-center gap-1">
                                  <FaClock size={9} />{' '}
                                  {conv.timestamp
                                    ? new Date(conv.timestamp).toLocaleString()
                                    : '—'}
                                </div>
                              </div>
                            </div>
                            <span
                              className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider border ${
                                conv.status === 'completed'
                                  ? 'bg-blue-50 text-blue-700 border-blue-200'
                                  : 'bg-green-50 text-green-700 border-green-200'
                              }`}
                            >
                              {conv.status}
                            </span>
                          </div>

                          <div className="space-y-2">
                            <div className="flex gap-2">
                              <div className="flex-shrink-0 mt-1">
                                <FaComments
                                  className="text-gray-300"
                                  size={14}
                                />
                              </div>
                              <div className="text-sm text-gray-700 bg-white p-2.5 rounded-lg border border-gray-200 italic flex-1">
                                “{conv.last_message}”
                              </div>
                            </div>

                            {conv.ai_reply && (
                              <div className="flex gap-2 pl-4">
                                <div className="flex-shrink-0 mt-1">
                                  <FaRobot
                                    className="text-purple-400"
                                    size={14}
                                  />
                                </div>
                                <div className="text-sm text-purple-800 bg-purple-50 p-2.5 rounded-lg border border-purple-200 font-medium flex-1">
                                  {conv.ai_reply}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
